import React, { useEffect, useState } from 'react';
import { CalendarDays } from 'lucide-react';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { toDate, toISODate, ukShort } from '../../lib/format';

// UK date field: shows DD/MM/YYYY, hands back an ISO yyyy-mm-dd string
export const UKDatePicker = ({
  value,
  onChange,
  placeholder = 'DD/MM/YYYY',
  label,
  className = '',
  testId,
  disabled = false,
}) => {
  const [text, setText] = useState(value ? ukShort(value) : '');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setText(value ? ukShort(value) : '');
  }, [value]);

  const commit = (raw) => {
    const trimmed = raw.trim();
    if (!trimmed) {
      onChange('');
      return;
    }
    const iso = toISODate(trimmed);
    if (iso) {
      onChange(iso);
      setText(ukShort(iso));
    } else {
      setText(value ? ukShort(value) : '');
    }
  };

  const selected = toDate(value) || undefined;

  return (
    <div className={`inline-flex flex-col gap-1 ${className}`}>
      {label && <label className="text-xs font-semibold text-slate-500">{label}</label>}
      <div className="relative">
        <input
          type="text"
          inputMode="numeric"
          disabled={disabled}
          value={text}
          placeholder={placeholder}
          aria-label={label || placeholder}
          onChange={(e) => setText(e.target.value)}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(text); } }}
          data-testid={testId}
          className="h-11 md:h-9 w-[9.5rem] rounded-[10px] border border-hair bg-white pl-3 pr-10 text-sm text-ink tabular-nums shadow-sm transition-colors placeholder:text-slate-400 focus-visible:outline-none focus-visible:border-gold focus-visible:ring-2 focus-visible:ring-gold/45 disabled:opacity-50"
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-label={`Open calendar${label ? ` for ${label}` : ''}`}
              data-testid={testId ? `${testId}-calendar` : undefined}
              className="absolute right-1 top-1/2 -translate-y-1/2 h-9 w-9 md:h-7 md:w-7 inline-flex items-center justify-center rounded-full text-gold-deep transition-colors hover:bg-cream"
            >
              <CalendarDays className="w-4 h-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-auto p-0 border-hair rounded-[12px]">
            <Calendar
              mode="single"
              weekStartsOn={1}
              selected={selected}
              defaultMonth={selected}
              onSelect={(date) => {
                if (!date) return;
                onChange(toISODate(date));
                setOpen(false);
              }}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

export default UKDatePicker;
